import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

/** Safe display of API values — never render objects as [object Object]. */
export function safeText(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

export function isViewer(user) {
  return String(user?.role || '').toLowerCase() === 'viewer';
}

export function isOwner(user) {
  return String(user?.role || '').toLowerCase() === 'owner';
}

export function canWrite(user) {
  const role = String(user?.role || '').toLowerCase();
  return role === 'owner' || role === 'staff';
}

export function LoadingBlock({ label = 'Loading…' }) {
  return (
    <div className="text-center py-24" role="status" aria-live="polite">
      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-500" />
      <p className="text-sm text-gray-500 mt-3">{label}</p>
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" role="alert">
      <span>{safeText(message, 'Something went wrong')}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400 rounded"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function PageHeader({ title, description, actions }) {
  return (
    <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div>
        <h1 className="text-3xl font-bold text-navy-900">{title}</h1>
        {description && <p className="text-gray-500 mt-1">{description}</p>}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export const FEDERATION_ONBOARDING_STEPS = [
  { key: 'profile', label: 'Organization profile', to: '/settings' },
  { key: 'timezone', label: 'Reporting timezone', to: '/settings' },
  { key: 'period', label: 'Reporting year or period', to: '/federation/settings' },
  { key: 'objective', label: 'First development objective', to: '/federation/objectives' },
  { key: 'program', label: 'First program', to: '/federation/programs' },
  { key: 'teams', label: 'Participating teams', to: '/federation/programs' },
  { key: 'access', label: 'Staff and program access', to: '/federation/settings' },
  { key: 'template', label: 'Reporting template', to: '/federation/settings' },
  { key: 'submission', label: 'First program submission', to: '/federation/submissions' },
  { key: 'finding', label: 'First organizational finding', to: '/federation/findings' },
  { key: 'action', label: 'First assigned action', to: '/federation/actions' },
  { key: 'review', label: 'First leadership review', to: '/federation/reviews' },
  { key: 'export', label: 'First report or export', to: '/federation/reports' },
];

export function EmptyGuide({ completedKeys = [] } = {}) {
  const done = new Set(completedKeys);
  return (
    <div className="border border-navy-200 rounded-xl bg-white px-6 py-10">
      <h2 className="text-lg font-semibold text-navy-900 mb-2 text-center">Get started with Federation</h2>
      <p className="text-gray-500 text-sm max-w-xl mx-auto mb-6 text-center">
        Complete these setup steps for a usable Federation workspace. No sample data is shown until you add real organization records.
      </p>
      <ol className="text-left max-w-xl mx-auto space-y-2 text-sm text-navy-900">
        {FEDERATION_ONBOARDING_STEPS.map((step, index) => {
          const complete = done.has(step.key);
          return (
            <li key={step.key} className="flex gap-3 items-start">
              <span className={`font-semibold w-6 ${complete ? 'text-green-700' : 'text-navy-700'}`}>
                {complete ? '✓' : index + 1}
              </span>
              <span>
                <Link
                  to={step.to}
                  className="font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400 rounded"
                >
                  {step.label}
                </Link>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export const PROGRAM_TYPES = [
  'Senior national team',
  'Youth national team',
  "Women's football",
  'Academy',
  'Coach development',
  'Regional development',
  'Other',
];

export const FINDING_STATUSES = [
  'New',
  'Accepted',
  'Action required',
  'Monitoring',
  'Closed',
  'Dismissed',
];

export const ACTION_STATUSES = [
  'Not started',
  'In progress',
  'Awaiting review',
  'Completed',
  'Cancelled',
];

export const OBJECTIVE_STATUSES = [
  'Active',
  'On track',
  'At risk',
  'Achieved',
  'Closed',
  'Archived',
];

export const REVIEW_OUTCOMES = [
  'Not reviewed',
  'Evidence pending',
  'Improving',
  'Resolved',
  'Not improved',
  'Inconclusive',
];

export const EXPORT_TYPES = [
  { value: 'program_summary', label: 'Program summary' },
  { value: 'quarterly_technical_review', label: 'Quarterly technical review' },
  { value: 'unresolved_findings', label: 'Unresolved findings' },
  { value: 'coaching_action_status', label: 'Coaching action status' },
  { value: 'reviewed_improvement', label: 'Reviewed improvement' },
  { value: 'program_execution', label: 'Program execution' },
];

export const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-200 focus:border-navy-400';

export const btnPrimary =
  'bg-navy-900 hover:bg-navy-800 disabled:opacity-60 text-white font-medium px-4 py-2 rounded text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400';

export const btnSecondary =
  'border border-navy-300 text-navy-900 hover:bg-navy-50 disabled:opacity-60 font-medium px-4 py-2 rounded text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400';
